/**
 * Armazenamento local do PDV (T3.1): IndexedDB com dois depósitos — `kv` (sessão, configuração, catálogo e último
 * comprovante) e `queue` (vendas feitas sem conexão, chave = `clientId`). Sem IndexedDB (aba privada restrita, testes)
 * cai para a memória: o PDV funciona, mas a fila não sobrevive a recarregar a página (o aviso aparece na tela).
 */

const DB_NAME = "cx-pdv";
const DB_VERSION = 1;
const KV = "kv";
const QUEUE = "queue";

export interface PdvStore {
  readonly persistent: boolean;
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  queueAll<T>(): Promise<T[]>;
  queuePut(id: string, value: unknown): Promise<void>;
  queueDelete(id: string): Promise<void>;
  /** Apaga sessão, configuração, catálogo e comprovante guardados (a fila fica: é do usuário que vendeu). */
  kvClear(): Promise<void>;
}

export function memoryStore(): PdvStore {
  const kv = new Map<string, unknown>();
  const q = new Map<string, unknown>();
  const clone = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));
  return {
    persistent: false,
    get: async <T>(k: string) => clone(kv.get(k)) as T | undefined,
    set: async (k, v) => void kv.set(k, clone(v)),
    queueAll: async <T>() => [...q.values()].map((v) => clone(v)) as T[],
    queuePut: async (id, v) => void q.set(id, clone(v)),
    queueDelete: async (id) => void q.delete(id),
    kvClear: async () => void kv.clear(),
  };
}

const req = <T>(r: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, DB_VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV);
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE);
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.onblocked = () => reject(new Error("IndexedDB bloqueado"));
  });
}

function idbStore(db: IDBDatabase): PdvStore {
  const tx = (store: string, mode: IDBTransactionMode) => db.transaction(store, mode).objectStore(store);
  return {
    persistent: true,
    get: <T>(k: string) => req(tx(KV, "readonly").get(k)) as Promise<T | undefined>,
    set: async (k, v) => void (await req(tx(KV, "readwrite").put(v, k))),
    queueAll: <T>() => req(tx(QUEUE, "readonly").getAll()) as Promise<T[]>,
    queuePut: async (id, v) => void (await req(tx(QUEUE, "readwrite").put(v, id))),
    queueDelete: async (id) => void (await req(tx(QUEUE, "readwrite").delete(id))),
    kvClear: async () => void (await req(tx(KV, "readwrite").clear())),
  };
}

let current: Promise<PdvStore> | null = null;

/** Abre uma vez por página; falhou = memória. */
export function pdvStore(): Promise<PdvStore> {
  current ??= (async () => {
    try {
      if (typeof indexedDB === "undefined") return memoryStore();
      return idbStore(await openDb());
    } catch {
      return memoryStore();
    }
  })();
  return current;
}

/**
 * Revisão de segurança T3.1: ao sair (ou com a sessão vencida) o aparelho não guarda mais a sessão, o catálogo (com
 * CPF/CNPJ de clientes) nem o comprovante, para o PDV não abrir sem conexão como o usuário anterior.
 */
export async function clearPdvCache(store?: PdvStore): Promise<void> {
  try {
    await (store ?? (await pdvStore())).kvClear();
  } catch {
    /* sem IndexedDB: nada guardado */
  }
}
